import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Loader2, Eye, EyeOff, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PasswordField = ({ id, name, label, value, visible, onToggle, onChange }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <div className="relative mt-1">
      <Input
        id={id}
        name={name}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        required
        className="pr-10"
        data-testid={`${id}-input`}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        tabIndex={-1}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  </div>
);

export default function AdminSettings() {
  const { getAuthHeaders, user } = useApp();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Business / GST settings
  const [biz, setBiz] = useState(null);
  const [savingBiz, setSavingBiz] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/admin/settings`, { headers: getAuthHeaders() })
      .then((res) => setBiz(res.data))
      .catch(() => setBiz(null));
  }, [getAuthHeaders]);

  const handleBizSave = async (e) => {
    e.preventDefault();
    setSavingBiz(true);
    try {
      const res = await axios.put(`${API_URL}/admin/settings`, {
        business_name: biz.business_name,
        business_address: biz.business_address,
        gst_number: biz.gst_number || '',
        gst_registered: biz.gst_registered,
        prices_include_gst: biz.prices_include_gst,
      }, { headers: getAuthHeaders() });
      setBiz(res.data);
      toast.success('Business & GST settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingBiz(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (form.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/auth/change-password`,
        { current_password: form.current_password, new_password: form.new_password },
        { headers: getAuthHeaders() }
      );
      toast.success('Password changed successfully');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6" data-testid="admin-settings">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
          Account Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage your account security and business details</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
      <div className="bg-white rounded-xl border border-border p-6 h-fit">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#EA580C]/10 flex items-center justify-center">
            <Lock className="h-5 w-5 text-[#EA580C]" />
          </div>
          <div>
            <h2 className="font-semibold">Change Password</h2>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordField id="current-password" name="current_password" label="Current Password" value={form.current_password} visible={show.current} onToggle={() => setShow((s) => ({ ...s, current: !s.current }))} onChange={handleChange} />
          <PasswordField id="new-password" name="new_password" label="New Password" value={form.new_password} visible={show.next} onToggle={() => setShow((s) => ({ ...s, next: !s.next }))} onChange={handleChange} />
          <PasswordField id="confirm-password" name="confirm_password" label="Confirm New Password" value={form.confirm_password} visible={show.confirm} onToggle={() => setShow((s) => ({ ...s, confirm: !s.confirm }))} onChange={handleChange} />
          <p className="text-xs text-muted-foreground">Use at least 8 characters. Choose something hard to guess.</p>
          <Button type="submit" disabled={saving} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white" data-testid="change-password-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {saving ? 'Saving...' : 'Update Password'}
          </Button>
        </form>
      </div>

      {/* Business & GST */}
      <div className="bg-white rounded-xl border border-border p-6 h-fit" data-testid="business-gst-settings">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#0B1120]/10 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-[#0B1120]" />
          </div>
          <div>
            <h2 className="font-semibold">Business &amp; GST</h2>
            <p className="text-sm text-muted-foreground">Shown on tax invoices</p>
          </div>
        </div>

        {biz ? (
          <form onSubmit={handleBizSave} className="space-y-4">
            <div>
              <Label htmlFor="business_name">Business Name</Label>
              <Input id="business_name" value={biz.business_name} onChange={(e) => setBiz({ ...biz, business_name: e.target.value })} className="mt-1" data-testid="business-name-input" />
            </div>
            <div>
              <Label htmlFor="business_address">Business Address</Label>
              <Textarea id="business_address" value={biz.business_address} onChange={(e) => setBiz({ ...biz, business_address: e.target.value })} rows={2} className="mt-1" data-testid="business-address-input" />
            </div>
            <div>
              <Label htmlFor="gst_number">GST Number</Label>
              <Input id="gst_number" value={biz.gst_number || ''} onChange={(e) => setBiz({ ...biz, gst_number: e.target.value })} placeholder="e.g. 123-456-789" className="mt-1" data-testid="gst-number-input" />
              <p className="text-xs text-muted-foreground mt-1">Required on tax invoices for GST-registered businesses.</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="gst_registered">GST Registered</Label>
                <p className="text-xs text-muted-foreground">Charge & report 15% GST</p>
              </div>
              <Switch id="gst_registered" checked={biz.gst_registered} onCheckedChange={(c) => setBiz({ ...biz, gst_registered: c })} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="prices_include_gst">Prices Include GST</Label>
                <p className="text-xs text-muted-foreground">Off = add 15% at checkout</p>
              </div>
              <Switch id="prices_include_gst" checked={biz.prices_include_gst} onCheckedChange={(c) => setBiz({ ...biz, prices_include_gst: c })} />
            </div>
            <Button type="submit" disabled={savingBiz} className="bg-[#0B1120] hover:bg-[#0B1120]/90 text-white" data-testid="save-business-btn">
              {savingBiz ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {savingBiz ? 'Saving...' : 'Save Business Details'}
            </Button>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">Loading...</p>
        )}
      </div>
      </div>
    </div>
  );
}
